import {Login} from "../../../auth/Login";
import {config, global} from "../../../appContext";
import {storeObservable} from "../../../util/sessionstore";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {getApplicationHash, getHash} from "../../../constants/RouteDefinitions";
import {nsLanguage} from "../../../util/nsLanguage";
import {getEadmRole, getSyncFieldValue} from "../../../util/misc";
import {hubSpotSupport} from "../../../tracking/HubSpotSupport";
import {toggleRightPanel as setRightPanelMode} from "../../../util/rightPanel";
import {Permission, SYSTEM_MEGA_EADM} from "../../../constants";
import * as ko from "knockout";
import getLanguageProperty = nsLanguage.getLanguageProperty;
import detailsHtml from './details.html?raw';

export let template: string = detailsHtml;

// Avatar colours — mapped to brand token scale
//
// Non-privileged (green / eAdm secondary axis):
//   brand-secondary-05  Elev          #d8f0c0  (spring tint, lightest)
//   brand-secondary-20  Ansatt        #8ecc66  --col-spring
//   brand-secondary-40  Superbruker   #638928  (mid-olive)
//   brand-secondary-60  Leder         #567623  --col-olive
//
// Privileged (navy / primary axis):
//   brand-primary-05    ServiceDesk   #a8b6cc  --col-bergen-sky
//   brand-primary-20    Admin         #2d5884  --col-id-blue
//   brand-primary-40    Partneradmin  #103f66  --col-dark-denim
//   brand-primary-60    SuperAdmin    #021d33  --col-blackened
//
// Users with an edu-role also receive a 3px apricot outline (#f8856a).

const ROLE_COLOR_PAIRS: [string[], string][] = [
    [['Superadministrator', 'SuperAdmin'],          '#021d33'],  // brand-primary-60
    [['Administrator', 'CompanyAdmin'],             '#2d5884'],  // brand-primary-20
    [['Partneradministrator', 'OrganizationAdmin'], '#103f66'],  // brand-primary-40
    [['Servicedeskbruker', 'SuperManager'],         '#a8b6cc'],  // brand-primary-05
    [['Leder', 'Manager'],                          '#567623'],  // brand-secondary-60
    [['Superbruker', 'SuperEmployee'],              '#638928'],  // brand-secondary-40
    [['Ansatt', 'Employee'],                        '#8ecc66'],  // brand-secondary-20
    [['Lærer'],                                     '#a8b6cc'],  // brand-primary-05 (edu)
    [['Elev'],                                      '#d8f0c0'],  // brand-secondary-05 (edu)
];

const ROLE_COLORS: Record<string, string> = Object.fromEntries(
    ROLE_COLOR_PAIRS.flatMap(([names, color]) => names.map(n => [n, color]))
);

// Permission enum → background color (fallback when role-name lookup fails)
const PERMISSION_COLORS: Record<number, string> = {
    [Permission.SuperAdmin]:        '#021d33',  // brand-primary-60
    [Permission.OrganizationAdmin]: '#103f66',  // brand-primary-40
    [Permission.CompanyAdmin]:      '#2d5884',  // brand-primary-20
    [Permission.SuperManager]:      '#a8b6cc',  // brand-primary-05
    [Permission.DepartmentAdmin]:   '#567623',  // brand-secondary-60
    [Permission.SuperEmployee]:     '#638928',  // brand-secondary-40
    [Permission.Employee]:          '#8ecc66',  // brand-secondary-20
};

const DEFAULT_AVATAR_COLOR = '#8ecc66';  // brand-secondary-20
const EDU_ROLE_BORDER_COLOR = '#f8856a'; // --col-apricot (eFeide primary)

const PERMISSION_ROLE_NAMES: Record<number, string> = {
    [Permission.SuperAdmin]:        'Superadministrator',
    [Permission.OrganizationAdmin]: 'Partneradministrator',
    [Permission.CompanyAdmin]:      'Administrator',
    [Permission.SuperManager]:      'Servicedeskbruker',
    [Permission.DepartmentAdmin]:   'Leder',
    [Permission.SuperEmployee]:     'Superbruker',
    [Permission.Employee]:          'Ansatt',
};
function getContrastTextColor(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1a1a2e' : '#ffffff';
}

function getLuminance(hex: string): number {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export class viewModel {
    login: Login;
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private sad = global.loginManager.sad.bind(global.loginManager);
    private showSA = storeObservable("show.super.adm.emulating");
    private readonly hashLogin: string | null;
    private readonly toggle: KnockoutObservable<boolean> = storeObservable('layout.right.toggle');
    
    private readonly userDropdownOpen: KnockoutObservable<boolean> = ko.observable(false);
    private readonly rightPanelToggle: KnockoutObservable<boolean> = storeObservable('layout.right.toggle');
    private readonly rightPanelMode: KnockoutObservable<'profile' | 'chat'> = storeObservable('layout.right.mode');
    private readonly detailsSettings: KnockoutObservable<boolean> = storeObservable('details.settings.toggle');

    private numNewTasksAndMessages = global.login.numNewTasksAndMessages;

    constructor() {
        this.hashLogin = getHash('formlogin');
        this.login = global.login;
    }

    showLogin = ko.pureComputed((): boolean => {
        // routeValidator is assigned during global.init — header may mount earlier.
        const componentName = global.routeValidator?.getRouteComponent()?.name ?? '';
        const isLoginComponent = [
            'component/login/form',
            'component/password/forgot/details',
            ].includes(componentName);
        const isLoggedIn = this.login.isLoggedIn();
        const isProcessing = this.login.isProcessing();
        return !isLoginComponent && !isLoggedIn && !isProcessing;
    });

    showLoggedInGui = ko.pureComputed((): boolean => {
        const isLoggedIn = this.login.isLoggedIn();
        const isProcessing = this.login.isProcessing();
        return isLoggedIn && !isProcessing;
    })

    isHubSpotAvailable = ko.pureComputed((): boolean => hubSpotSupport.isChatAvailable());

    isProfilePanelActive = ko.pureComputed((): boolean =>
        !!this.rightPanelToggle() && this.rightPanelMode() === 'profile'
    );

    isChatPanelActive = ko.pureComputed((): boolean =>
        !!this.rightPanelToggle() && this.rightPanelMode() === 'chat'
    );

    copilotButtonLabel = ko.pureComputed((): string => 'Support');

    onSAClick() {
        this.login.doLogout();
    }

    onHideSAClick() {
        this.showSA(false);
    }

    private toggleRightPanel(mode: 'profile' | 'chat'): void {
        setRightPanelMode(mode);
    }

    onProfilePanelClick(): void {
        this.toggleRightPanel('profile');
    }

    onCopilotClick(): void {
        this.toggleRightPanel('chat');
    }

    onOpenSettingsPanelClick() {
        this.rightPanelMode('profile');
        this.rightPanelToggle(true);
        this.detailsSettings(true);
        this.userDropdownOpen(false);
        return false;
    }

    onDropdownOverlayClick() {
        this.userDropdownOpen(false);
        return true;
    }

    onDropdownContentClick(vm: viewModel, e: MouseEvent) {
        const target = e.target as HTMLElement;
        const link = target.closest('a');
        
        if (link && link.getAttribute('href')?.startsWith('#')) {
            setTimeout(() => {
                this.userDropdownOpen(false);
            }, 100);
        }
        
        e.stopPropagation();
        return true;
    }

    onLogoutClick() {
        global.login.doLogout();
    }

    onMobileNavClose() {
        this.rightPanelToggle(false);
        return true; // allow default navigation
    }

    onMobileNavLogout() {
        this.rightPanelToggle(false);
        global.login.doLogout();
    }

    private onSkipBlockClick(vm: viewModel, e: JQuery.TriggeredEvent) {
        const query = e.currentTarget.dataset.linkTarget;
        if (!query) {
            return;
        }
        const target = document.querySelector(query);
        if (!target) {
            return;
        }
        target.scrollIntoView({behavior: "smooth", block: 'start'});
        (target as HTMLElement).focus();
    }

    private getLogoUrl = ko.pureComputed((): string => {
        if (global.login.isLoggedIn()) {
            return getApplicationHash();
        }
        return getHash('formlogin') ?? '';
    });

    private getHref_documentation(): string {
        return getLanguageProperty(config.URL_DOCUMENTATION)
    }

    // Initials: first letter of first name + first letter of last name
    private getInitials(): string {
        const user = this.login.user();
        if (!user) return '';
        const first = getSyncFieldValue('GivenName', user.syncfields) || '';
        const last = getSyncFieldValue('Surname', user.syncfields) || '';
        return ((first.charAt(0) || '') + (last.charAt(0) || '')).toUpperCase();
    }

    // Avatar background color based on user's role
    private getAvatarBgColor(): string {
        const org = this.login.selectedOrganization?.();
        if (!org?.systempermission) return DEFAULT_AVATAR_COLOR;

        const eadmPerms = org.systempermission.filter(sp => sp.system?.name === SYSTEM_MEGA_EADM);
        if (!eadmPerms.length) return DEFAULT_AVATAR_COLOR;

        const highest = eadmPerms.reduce((a, b) => a.role.roleenum > b.role.roleenum ? a : b);
        const roleName = highest.role.name;

        // Try role name first
        if (roleName && ROLE_COLORS[roleName]) {
            return ROLE_COLORS[roleName];
        }

        // Fallback to Permission enum
        return PERMISSION_COLORS[highest.role.roleenum] || DEFAULT_AVATAR_COLOR;
    }

    // Text color for contrast against avatar background
    private getAvatarTextColor(): string {
        return getContrastTextColor(this.getAvatarBgColor());
    }

    // Border color adapted to avatar brightness so dark roles do not look visually smaller.
    private getAvatarBorderColor(): string {
        const luminance = getLuminance(this.getAvatarBgColor());
        return luminance > 0.5 ? 'rgba(0, 0, 0, 0.18)' : 'rgba(255, 255, 255, 0.35)';
    }

    // Returns a CSS outline value when the logged-in user has an edu-role,
    // so the apricot border (eFeide primary colour) is applied.
    // TODO: replace stub with real edu-role detection when the field is available.
    private getAvatarEduOutline(): string | null {
        // const hasEduRole = /* check user.edurolle or similar */ false;
        const hasEduRole = false;
        return hasEduRole ? `3px solid ${EDU_ROLE_BORDER_COLOR}` : null;
    }

    // Full display name for dropdown header
    private getDisplayName(): string {
        const user = this.login.user();
        if (!user) return '';
        const firstName = getSyncFieldValue('GivenName', user.syncfields) || '';
        const lastName = getSyncFieldValue('Surname', user.syncfields) || '';
        return `${firstName} ${lastName}`.trim();
    }

    private getEmulatedRoleName(): string {
        const org = this.login.selectedOrganization?.();
        if (!org?.systempermission) return '';
        const eadmPerms = org.systempermission.filter(sp => sp.system?.name === SYSTEM_MEGA_EADM);
        if (!eadmPerms.length) return '';
        const highest = eadmPerms.reduce((a, b) => a.role.roleenum > b.role.roleenum ? a : b);
        return highest.role.name || PERMISSION_ROLE_NAMES[highest.role.roleenum] || '';
    }

    dispose() {
        this.kosub.unsuscribeAll();
    }
}
